// TOUR OVERLAY — dims the screen with four panes AROUND the spotlit real element (the element itself stays visible and interactive), and
// places a compact explanation bubble beside it (right → left → below → above, clamped to the viewport). role=dialog, keyboard: Tab/Shift+Tab
// cycle inside the bubble (except during TRY IT, when the student may reach the real control), Enter = Next, Escape = Skip/close.
// Reduced motion: no transitions. Nothing here reads or writes pedagogical state.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { anchor, reducedMotion } from "./demoActions";

const DIM = "rgba(28, 25, 23, 0.55)";
const PAD = 6;
const BUBBLE_W = 340;
const GAP = 14;

// SPOTLIGHT GEOMETRY — the live target's own box (getBoundingClientRect) plus a small, constant padding; computed by the caller on every measure.
export const spotlightFor = (rect, vw, vh, pad = PAD) => (rect ? { top: Math.max(0, rect.top - pad), left: Math.max(0, rect.left - pad), right: Math.min(vw, rect.left + rect.width + pad), bottom: Math.min(vh, rect.top + rect.height + pad) } : null);
const same = (p, r) => p && Math.abs(p.top - r.top) < 0.5 && Math.abs(p.left - r.left) < 0.5 && Math.abs(p.width - r.width) < 0.5 && Math.abs(p.height - r.height) < 0.5;

// THE LIVE TARGET RECT — never a cached coordinate: re-measured on scroll (any scroll container), resize, the target's own resize (ResizeObserver),
// DOM changes (a tool opening / a view change replaces the target, MutationObserver) and a slow tick as the last resort. The target is scrolled into
// view (restrained, reduced-motion aware) once per element identity, BEFORE the box is trusted; a new element under the same name is scrolled again.
export function useAnchorRect(anchorName) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!anchorName) { setRect(null); return undefined; }
    let alive = true, frame = 0, scrolledFor = null, ro = null, observed = null;
    const measure = () => {
      frame = 0;
      if (!alive) return;
      const el = anchor(anchorName);
      if (!el) { setRect(null); return; }
      if (scrolledFor !== el) { scrolledFor = el; try { el.scrollIntoView({ block: "center", inline: "nearest", behavior: reducedMotion() ? "auto" : "smooth" }); } catch { /* ignore */ } }
      if (ro && observed !== el) { if (observed) ro.unobserve(observed); ro.observe(el); observed = el; }
      const r = el.getBoundingClientRect();
      setRect((p) => (same(p, r) ? p : { top: r.top, left: r.left, width: r.width, height: r.height }));
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    if (typeof ResizeObserver !== "undefined") ro = new ResizeObserver(schedule);
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
    if (mo) mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "class", "style", "data-tour"] });
    measure();
    const iv = setInterval(schedule, 250);
    window.addEventListener("resize", schedule); window.addEventListener("scroll", schedule, true);
    return () => {
      alive = false; clearInterval(iv); if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule); window.removeEventListener("scroll", schedule, true);
      if (ro) ro.disconnect(); if (mo) mo.disconnect();
    };
  }, [anchorName]);
  return rect;
}

export function placeBubble(rect, vw, vh, bw, bh) {
  const margin = 12;
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  if (!rect) return { top: clamp((vh - bh) / 2, margin, Math.max(margin, vh - bh - margin)), left: clamp((vw - bw) / 2, margin, Math.max(margin, vw - bw - margin)), side: "center" };
  const right = rect.left + rect.width + GAP, left = rect.left - GAP - bw;
  if (right + bw + margin <= vw) return { top: clamp(rect.top, margin, Math.max(margin, vh - bh - margin)), left: right, side: "right" };
  if (left >= margin) return { top: clamp(rect.top, margin, Math.max(margin, vh - bh - margin)), left, side: "left" };
  const below = rect.top + rect.height + GAP;
  const l = clamp(rect.left, margin, Math.max(margin, vw - bw - margin));
  if (below + bh + margin <= vh) return { top: below, left: l, side: "below" };
  const above = rect.top - GAP - bh;
  if (above >= margin) return { top: above, left: l, side: "above" };
  return { top: clamp(vh - bh - margin, margin, vh), left: l, side: "over" };      // no room anywhere: bottom of the viewport, never off-screen
}

export function TourOverlay({ beat, stopIndex, stopCount, beatIndex, beatCount, onNext, onBack, onSkip, canBack, isLast, busy, contextual, tryDone }) {
  const rect = useAnchorRect(beat && beat.anchor);
  const bubbleRef = useRef(null);
  const [size, setSize] = useState({ w: BUBBLE_W, h: 200 });
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => { const f = () => setVp({ w: window.innerWidth, h: window.innerHeight }); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  useLayoutEffect(() => { if (bubbleRef.current) { const r = bubbleRef.current.getBoundingClientRect(); if (Math.abs(r.width - size.w) > 1 || Math.abs(r.height - size.h) > 1) setSize({ w: r.width, h: r.height }); } });
  useEffect(() => { if (bubbleRef.current) bubbleRef.current.focus({ preventScroll: true }); }, [stopIndex, beatIndex]);
  if (!beat) return null;
  const isTry = beat.mode === "try";
  const bw = Math.min(BUBBLE_W, vp.w - 24);
  const pos = placeBubble(rect, vp.w, vp.h, bw, size.h);
  const hole = spotlightFor(rect, vp.w, vp.h);
  const panes = hole ? [
    { top: 0, left: 0, width: vp.w, height: hole.top },
    { top: hole.bottom, left: 0, width: vp.w, height: Math.max(0, vp.h - hole.bottom) },
    { top: hole.top, left: 0, width: hole.left, height: hole.bottom - hole.top },
    { top: hole.top, left: hole.right, width: Math.max(0, vp.w - hole.right), height: hole.bottom - hole.top },
  ] : [{ top: 0, left: 0, width: vp.w, height: vp.h }];
  const trans = reducedMotion() ? "none" : "top 180ms ease, left 180ms ease, width 180ms ease, height 180ms ease";
  const focusables = () => bubbleRef.current ? [...bubbleRef.current.querySelectorAll("button:not([disabled])")] : [];
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onSkip(); return; }
    if (e.key === "Enter" && e.target === bubbleRef.current) { e.preventDefault(); onNext(); return; }
    if (e.key === "Tab" && !isTry) {
      const fs = focusables(); if (!fs.length) return;
      const i = fs.indexOf(document.activeElement);
      if (e.shiftKey && (i <= 0)) { e.preventDefault(); fs[fs.length - 1].focus(); }
      else if (!e.shiftKey && i === fs.length - 1) { e.preventDefault(); fs[0].focus(); }
    }
  };
  const tid = `tour-title-${stopIndex}-${beatIndex}`, did = `tour-body-${stopIndex}-${beatIndex}`;
  return (
    <div data-testid="tour-overlay" data-anchor={beat.anchor || ""} data-side={pos.side} className="fixed inset-0 z-[90]" style={{ pointerEvents: "none" }}>
      {/* the dim panes and the spotlight follow the LIVE measurement with no positional transition: while the page scrolls or re-lays out, the
          highlight must hug the target on every frame, never trail behind it */}
      {panes.map((p, i) => <div key={i} data-testid="tour-dim" style={{ position: "fixed", background: DIM, pointerEvents: "auto", ...p }} aria-hidden="true" />)}
      {hole ? <div data-testid="tour-spotlight" aria-hidden="true" data-top={hole.top} data-left={hole.left} data-width={hole.right - hole.left} data-height={hole.bottom - hole.top}
        style={{ position: "fixed", top: hole.top, left: hole.left, width: hole.right - hole.left, height: hole.bottom - hole.top, borderRadius: 10, boxShadow: "0 0 0 2px rgba(255,255,255,.9), 0 0 0 4px #8C3A2A", pointerEvents: "none" }} /> : null}
      <div ref={bubbleRef} tabIndex={-1} role="dialog" aria-modal="false" aria-labelledby={tid} aria-describedby={did} onKeyDown={onKey}
        data-testid="tour-bubble" data-mode={beat.mode || "explain"}
        style={{ position: "fixed", top: pos.top, left: pos.left, width: bw, pointerEvents: "auto", transition: trans, outline: "none" }}
        className="rounded-lg border border-stone-200 bg-[#FCFAF7] shadow-xl px-4 py-3.5 text-stone-800">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400" data-testid="tour-progress">
              {contextual ? "TWS \u00b7 a quick introduction" : `Tour \u00b7 ${stopIndex + 1} of ${stopCount}${beatCount > 1 ? ` \u00b7 ${beatIndex + 1}/${beatCount}` : ""}`}
            </p>
            {beat.title ? <h2 id={tid} className="font-serif-display text-[17px] leading-snug text-stone-900 mt-0.5">{beat.title}</h2> : <span id={tid} className="sr-only">Tour step</span>}
          </div>
          <button type="button" data-testid="tour-close" aria-label={contextual ? "Close" : "Skip tour"} onClick={onSkip} className="text-stone-400 hover:text-stone-700 text-[16px] leading-none px-1">×</button>
        </div>
        <div id={did} className="mt-2 space-y-2 text-[13.5px] leading-relaxed">
          {(beat.body || []).map((line, i) => <p key={i}>{line}</p>)}
          {isTry ? <p data-testid="tour-try-hint" className="text-[12.5px] italic text-[#8C3A2A]">{beat.tryHint}{tryDone ? " \u2014 done." : ""}</p> : null}
          {busy ? <p data-testid="tour-busy" className="text-[12px] text-stone-500" aria-live="polite">TWS is showing you…</p> : null}
        </div>
        <div className="mt-3 flex items-center gap-2">
          {!contextual ? <button type="button" data-testid="tour-skip" onClick={onSkip} className="text-[11.5px] text-stone-500 hover:text-stone-800 mr-auto">Skip tour</button> : <span className="mr-auto" />}
          {!contextual && canBack ? <button type="button" data-testid="tour-back" onClick={onBack} disabled={busy} className="rounded-sm border border-stone-300 bg-white px-3 py-1.5 text-[12px] text-stone-700 hover:border-[#8C3A2A] disabled:opacity-40">Back</button> : null}
          <button type="button" data-testid="tour-next" onClick={onNext} disabled={busy}
            className="rounded-sm bg-[#8C3A2A] px-4 py-1.5 text-[12px] uppercase tracking-[0.1em] text-white hover:bg-[#6B2C20] disabled:opacity-40">
            {beat.cta ? beat.cta : contextual ? (isLast ? "Got it" : "Next") : isLast ? "Finish" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}
